import { router } from "@inertiajs/react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Field } from "@/components/steel"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export function AddHoursDialog({
  employeeId,
  employees,
  onOpenChange,
  open,
  projects,
}: {
  employeeId?: string
  employees?: { id: string; name: string }[]
  onOpenChange: (open: boolean) => void
  open: boolean
  projects: { id: string; name: string }[]
}) {
  const [chosenEmployee, setChosenEmployee] = useState("")
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [description, setDescription] = useState("")
  const [hours, setHours] = useState("")
  const [projectId, setProjectId] = useState("")
  const [submittedBy, setSubmittedBy] = useState<"employee" | "employer">("employee")

  const resolvedEmployee = employeeId ?? chosenEmployee

  const submit = () => {
    if (!resolvedEmployee || !projectId || !hours || !description) return
    router.post(
      "/time_entries",
      { date, description, employeeId: resolvedEmployee, hours: Number(hours), projectId, submittedBy },
      {
        preserveScroll: true,
        onSuccess: () => {
          setDescription("")
          setHours("")
          setProjectId("")
          onOpenChange(false)
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Log hours</DialogTitle>
          <DialogDescription>Add a time entry for this employee.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {employees ? (
            <Field label="Employee">
              <Select value={chosenEmployee} onValueChange={setChosenEmployee}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((employee) => (
                    <SelectItem key={employee.id} value={employee.id}>
                      {employee.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          ) : null}
          <Field label="Project">
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="Date">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label="Hours">
              <Input
                className="num"
                type="number"
                min="0.5"
                max="24"
                step="0.5"
                placeholder="8"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
              />
            </Field>
          </div>
          <Field label="Description">
            <Input
              placeholder="What was worked on"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <Field label="Submitted by">
            <Select value={submittedBy} onValueChange={(v) => setSubmittedBy(v as "employee" | "employer")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="employee">Employee</SelectItem>
                <SelectItem value="employer">Employer</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={!resolvedEmployee || !projectId || !hours || !description}>
            Submit entry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
